import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Card,
  TextField,
  Select,
  Badge as PolarisBadge,
  Button as PolarisButton,
  Banner,
} from "@shopify/polaris";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import PaginationControls from "components/pagination/PaginationControls";
import { Column } from "types/Datatable";
import { PHONE_PAGE_GUTTER, PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchTrainingTests,
  TrainingTest,
  TrainingTestStatus,
} from "services/work-security-training.service";
import { WorkSecurityListPagination } from "services/work-security.service";
import { WorkSecurityArea, WorkSecurityAreaScopeProps } from "services/work-security-areas.service";

function statusLabel(t: any, status: TrainingTestStatus) {
  switch (status) {
    case "draft":
      return t("training_status_draft", { defaultValue: "Draft" });
    case "active":
      return t("training_status_active", { defaultValue: "Active" });
    case "archived":
      return t("training_status_archived", { defaultValue: "Archived" });
    default:
      return status;
  }
}

function statusTone(status: TrainingTestStatus): any {
  if (status === "active") return "success";
  if (status === "archived") return "info";
  return undefined;
}

interface Props extends WorkSecurityAreaScopeProps {
  /** Accessible areas (from the module header's useAreaScope) — used to label the "Sritis" column. */
  areas?: WorkSecurityArea[];
}

/** Falls back to a generic label for a test whose area was since archived (excluded from `areas`). */
function areaLabel(areas: WorkSecurityArea[], t: any, areaId?: string | null): string {
  const found = areaId ? areas.find((a) => a.id === areaId) : undefined;
  return found ? found.name : t("area_unknown", { defaultValue: "Other sphere" });
}

// `areaId`/`selectedArea` follow the PR-2 §P1 hard rule (see useAreaScope):
// empty when there is nothing to disambiguate, so the fetch call below is
// unaffected for the common single-area company.
export default function WorkSecurityTrainingPanel({
  areaId,
  selectedArea,
  areas = [],
}: Props = {}) {
  const { companyId } = useParams<{ companyId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  // "All areas" mode, and only worth a column when there is more than one
  // sphere to disambiguate — matches the module header's own switcher gate.
  const showAreaColumn = !areaId && areas.length > 1;

  const [tests, setTests] = useState<TrainingTest[]>([]);
  const [pagination, setPagination] =
    useState<WorkSecurityListPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });
  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const requestRef = useRef(0);

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];

  // Switching spheres resets pagination the same way switching companies
  // does (see work-security.tsx's own roles-tab reset effect, PR-2 §P1).
  useEffect(() => {
    setPaginationState({ page: 1, pageSize: 20 });
  }, [areaId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTextSearch((current) => {
        if (current === textSearch) return current;
        setPaginationState((prev) => ({ ...prev, page: 1 }));
        return textSearch;
      });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [textSearch]);

  const load = useCallback(async () => {
    if (!companyId) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fetchTrainingTests(companyId, {
        page,
        pageSize,
        textSearch: debouncedTextSearch,
        status: statusFilter || undefined,
        ...(areaId ? { areaId } : {}),
      });
      if (reqId !== requestRef.current) return;
      setTests(data.tests || []);
      setPagination(data.pagination || null);
    } catch (err) {
      console.error(err);
      if (reqId !== requestRef.current) return;
      setLoadError(true);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [companyId, page, pageSize, debouncedTextSearch, statusFilter, areaId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = () => {
    dispatch(setModalxComponent("TrainingTestFormModal"));
    dispatch(
      setModalxComponentProps({
        // PR-2 §P4: read-only "Sritis" plaque when scoped to one sphere, or a
        // required Select in "all spheres" mode.
        areas,
        selectedArea,
        onSuccess: load,
      })
    );
    dispatch(
      setModalxOptions({
        title: t("create_training_test", { defaultValue: "Create training test" }),
        size: "md",
      })
    );
  };

  const columns: Column[] = [
    {
      key: "name",
      name: t("name", { defaultValue: "Name" }),
      type: "clickableColumn",
      url: (item: any) => item.viewUrl,
      mobileView: "topLeft",
    },
    {
      key: "status",
      name: t("status", { defaultValue: "Status" }),
      type: "custom",
      mobileView: "topRight",
      render: (item: TrainingTest) => (
        <PolarisBadge tone={statusTone(item.status)}>
          {statusLabel(t, item.status)}
        </PolarisBadge>
      ),
    },
    ...(showAreaColumn
      ? [
          {
            key: "area",
            name: t("area_switcher_label", { defaultValue: "Sritis" }),
            type: "custom" as const,
            render: (item: TrainingTest) => (
              <PolarisBadge>{areaLabel(areas, t, item.area)}</PolarisBadge>
            ),
          },
        ]
      : []),
    {
      key: "questionsCount",
      name: t("training_col_questions", { defaultValue: "Questions" }),
    },
    {
      key: "rolesCount",
      name: t("training_col_roles", { defaultValue: "Roles" }),
    },
    {
      key: "assignmentCount",
      name: t("assigned", { defaultValue: "Assigned" }),
    },
    {
      key: "passedCount",
      name: t("training_col_passed", { defaultValue: "Passed" }),
    },
    {
      key: "durationMinutes",
      name: t("training_col_duration", { defaultValue: "Duration" }),
      type: "custom",
      mobileView: "bottomLeft",
      render: (item: TrainingTest) =>
        `${item.durationMinutes} ${t("min", { defaultValue: "min" })}`,
    },
    {
      key: "createdAtIso",
      name: t("created_at", { defaultValue: "Created At" }),
      type: "datetime",
      mobileView: "bottomRight",
    },
  ];

  const collection = tests.map((test) => ({
    ...test,
    viewUrl: `/app/${companyId}/work-security/training/${test.id}`,
    createdAtIso: test.createdAt
      ? new Date(test.createdAt).toISOString()
      : "",
  }));

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : tests.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
        ? Math.ceil(totalItems / pageSize)
        : 0;
  const hasNextPage = totalPages > 0 ? page < totalPages : false;
  const hasPreviousPage = page > 1;
  const shouldShowPagination = Boolean(pagination && totalItems > 0);

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState({ page: 1, pageSize: nextPageSize });
    }
  };

  return (
    <div className="space-y-4">
      <div className={`mt-4 flex justify-end ${PHONE_PAGE_GUTTER}`}>
        <PolarisButton variant="primary" onClick={handleCreate}>
          {t("create_training_test", { defaultValue: "Create training test" })}
        </PolarisButton>
      </div>

      {loadError && tests.length > 0 && (
        <Banner tone="critical">
          {t("training_tests_load_failed", {
            defaultValue: "Unable to refresh training tests.",
          })}
        </Banner>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-2 items-end">
          <div className="md:col-span-2">
            <TextField
              label={t("search", { defaultValue: "Search" })}
              value={textSearch}
              onChange={setTextSearch}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTextSearch("")}
              placeholder={t("search_training_tests", {
                defaultValue: "Search by name or description…",
              })}
            />
          </div>
          <Select
            label={t("status", { defaultValue: "Status" })}
            options={[
              { label: t("all", { defaultValue: "All" }), value: "" },
              {
                label: statusLabel(t, "draft"),
                value: "draft",
              },
              {
                label: statusLabel(t, "active"),
                value: "active",
              },
              {
                label: statusLabel(t, "archived"),
                value: "archived",
              },
            ]}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPaginationState((prev) => ({ ...prev, page: 1 }));
            }}
          />
        </div>
      </Card>

      <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
        {loading && tests.length === 0 ? (
          <DataTableSkeleton rows={8} columns={7} />
        ) : loadError && tests.length === 0 ? (
          <div className="text-center py-16 px-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {t("training_tests_load_failed", {
                defaultValue: "Unable to load training tests.",
              })}
            </h3>
            <div className="mt-4">
              <PolarisButton onClick={load}>
                {t("retry", { defaultValue: "Retry" })}
              </PolarisButton>
            </div>
          </div>
        ) : tests.length === 0 ? (
          <div className="text-center py-16 px-4">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {t("no_training_tests", {
                defaultValue: "No training tests yet",
              })}
            </h3>
            <p className="mt-1 text-sm text-zinc-500">
              {t("no_training_tests_hint", {
                defaultValue:
                  "Create a test, attach materials, add questions, then link roles.",
              })}
            </p>
            <div className="mt-4">
              <PolarisButton variant="primary" onClick={handleCreate}>
                {t("create_training_test", {
                  defaultValue: "Create training test",
                })}
              </PolarisButton>
            </div>
          </div>
        ) : (
          <>
            <DataTable
              collection={collection}
              rowUrl={(row: any) => row.viewUrl}
              columns={columns}
            />
            {shouldShowPagination && (
              <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={totalItems}
                  totalPages={totalPages}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={pageSizeOptions}
                  isLoading={loading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
